// RigSentinel Calc — Daily Drilling Report deterministic polishing + assembly.
// All polishing is offline / template-based. An extension hook is exposed at
// the bottom for a future LLM-assisted polish step (Emergent LLM key, etc).
// The polish step NEVER invents new facts: it only normalises wording the user
// already typed (capitalisation, common drilling abbreviations, sentence join).

/**
 * Three-state checkbox rendering.
 *
 * The DDR's checkbox groups start as empty objects (`bop: {}`, `weather: {}`,
 * `npt: {}` in DailyDrillingReport.jsx), so an untouched checkbox is
 * `undefined` — NOT `false`. A plain `v ? "Yes" : "No"` therefore asserted a
 * negative the crew never recorded, which on a safety-critical line such as
 * "Kick / Influx Observed" reads as a positive statement that no kick was
 * seen. Only an explicit `false` (ticked then unticked) is a real "No".
 */
function _yn(v, yes = "Yes") {
  if (v === true) return yes;
  if (v === false) return "No";
  return "Not entered";
}

// ----- 1. Light-touch text polish (deterministic, no fact invention) ---------
const ABBR = [
  // common rig-floor abbreviations safe to expand
  [/\bhse\b/gi, "HSE"],
  [/\bbop\b/gi, "BOP"],
  [/\bbu\b/gi, "BU"],
  [/\bmd\b/gi, "MD"],
  [/\btvd\b/gi, "TVD"],
  [/\brop\b/gi, "ROP"],
  [/\bspp\b/gi, "SPP"],
  [/\bspm\b/gi, "SPM"],
  [/\bppg\b/gi, "ppg"],
  [/\brpm\b/gi, "RPM"],
  [/\bgpm\b/gi, "gpm"],
  [/\bnpt\b/gi, "NPT"],
  [/\bppe\b/gi, "PPE"],
  [/\bptw\b/gi, "PTW"],
  [/\bsimops\b/gi, "SIMOPS"],
  [/\brkb\b/gi, "RKB"],
  [/\bml\b/gi, "ML"],
  [/\bobm\b/gi, "OBM"],
  [/\bwbm\b/gi, "WBM"],
  [/\bsbm\b/gi, "SBM"],
  [/\bmwd\b/gi, "MWD"],
  [/\blwd\b/gi, "LWD"],
  [/\brss\b/gi, "RSS"],
  [/\biadc\b/gi, "IADC"],
  [/\bpv\b/gi, "PV"],
  [/\byp\b/gi, "YP"],
  [/\btoh\b/gi, "TOH"],
  [/\btih\b/gi, "TIH"],
  [/\bbhA\b/gi, "BHA"],
  [/\bbha\b/gi, "BHA"],
  [/\btfa\b/gi, "TFA"],
  [/\b\?(?!\?)/g, "?"],
];

function expandAbbr(s) {
  let out = s;
  ABBR.forEach(([rx, sub]) => { out = out.replace(rx, sub); });
  return out;
}

function fixCaps(s) {
  if (!s) return "";
  return s.replace(/(^|[.!?]\s+)([a-z])/g, (_, p, c) => p + c.toUpperCase());
}

function ensureTerminator(s) {
  const t = s.trim();
  if (!t) return "";
  return /[.!?]$/.test(t) ? t : t + ".";
}

// Light "professional sentences from rough notes" — split on newlines/semicolons,
// trim, dedupe, ensure terminators, restore caps, expand safe abbreviations.
// Does NOT add facts. Empty / blank input → null.
export function polishProse(input) {
  if (!input || !String(input).trim()) return null;
  const raw = String(input)
    .replace(/\r/g, "")
    .split(/[\n;]+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const seen = new Set();
  const sentences = [];
  for (const line of raw) {
    // Split lines that already contain multiple sentences.
    const parts = line.split(/(?<=[.!?])\s+(?=[A-Z0-9])/).map((p) => p.trim()).filter(Boolean);
    for (const p of parts) {
      const polished = ensureTerminator(fixCaps(expandAbbr(p)));
      const key = polished.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        sentences.push(polished);
      }
    }
  }
  return sentences.length ? sentences.join(" ") : null;
}

// "Not entered" placeholder — used everywhere a critical field is empty.
export const MISSING = "Not entered.";
const orMissing = (v) => (v && String(v).trim() ? String(v).trim() : MISSING);
const orNull = (v) => (v && String(v).trim() ? String(v).trim() : null);

// ----- 2. Compose each report section ----------------------------------------
function fmtDate(d) {
  if (!d) return MISSING;
  return d;
}

function safeNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function unitFor(system, imp, met) {
  return system === "metric" ? met : imp;
}

// ----- 24-hour activity helpers ----------------------------------------------
export function calcDuration(start, end) {
  // start/end are "HH:MM" strings. Returns hours (decimal), wrapping past midnight.
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some((x) => Number.isNaN(x))) return null;
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 100) / 100;
}

export function sumActivities(rows) {
  let total = 0;
  let npt = 0;
  let standby = 0;
  rows.forEach((r) => {
    const d = safeNum(r.duration);
    if (d === null) return;
    total += d;
    if (r.npt) npt += d;
    if (r.activityType && /standby/i.test(r.activityType)) standby += d;
  });
  return {
    total: Math.round(total * 100) / 100,
    npt: Math.round(npt * 100) / 100,
    standby: Math.round(standby * 100) / 100,
  };
}

// ----- 3. Build the full polished report -------------------------------------
export function buildReport(state) {
  const sys = state.header?.unitSystem === "metric" ? "metric" : "imperial";
  const depthU = unitFor(sys, "ft", "m");
  const lines = [];

  const push = (s) => { if (s) lines.push(s); };
  const pushSection = (label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);

  // ----- Header -----
  pushSection("Report Header");
  const h = state.header || {};
  push(`Report Date: ${fmtDate(h.reportDate)}`);
  push(`Report Number: ${orMissing(h.reportNumber)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Contractor: ${orMissing(h.contractor)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Well: ${orMissing(h.wellName)}  ·  Field/Block: ${orMissing(h.field)}`);
  push(`Location: ${orMissing(h.location)}  ·  Country/Region: ${orMissing(h.country)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)} (${orMissing(h.position)})`);
  push(`Tour / Shift: ${orMissing(h.tour)}`);
  push(`Period: ${orMissing(h.periodStart)} → ${orMissing(h.periodEnd)} (${orMissing(h.timeZone)})`);
  push(`Unit System: ${sys === "metric" ? "Metric" : "Imperial"}`);

  // ----- Well Status Summary -----
  pushSection("Well Status Summary");
  const w = state.well || {};
  push(`Well Type: ${orMissing(w.wellType)}  ·  Status: ${orMissing(w.wellStatus)}`);
  push(`Hole Section: ${orMissing(w.holeSection)}  ·  Hole Size: ${orMissing(w.holeSize)}`);
  push(`Current MD: ${orMissing(w.currentMd)} ${w.currentMd ? depthU : ""}`);
  push(`Current TVD: ${orMissing(w.currentTvd)} ${w.currentTvd ? depthU : ""}`);
  push(`Previous Depth: ${orMissing(w.previousDepth)} ${w.previousDepth ? depthU : ""}`);
  push(`Footage Drilled Today: ${orMissing(w.footageDrilled)} ${w.footageDrilled ? depthU : ""}`);
  if (w.waterDepth) push(`Water Depth: ${w.waterDepth} ${depthU}`);
  if (w.rkbElevation) push(`RKB / Rig Floor Elevation: ${w.rkbElevation} ${depthU}`);
  if (w.mudlineRef) push(`Mudline / Seabed Reference: ${w.mudlineRef}`);
  push(`Last Casing: ${orMissing(w.casingSize)}  ·  Shoe MD: ${orMissing(w.shoeMd)} ${w.shoeMd ? depthU : ""}  ·  Shoe TVD: ${orMissing(w.shoeTvd)} ${w.shoeTvd ? depthU : ""}`);
  push(`Formation: ${orMissing(w.formation)}  ·  Target Depth: ${orMissing(w.targetDepth)}`);
  push(`Planned Next Casing Point: ${orMissing(w.nextCasingPoint)}`);

  // ----- Operations Summary -----
  pushSection("Operations Summary");
  const ops = state.ops || {};
  push(`Main Operation Type: ${orMissing(ops.mainOperation)}`);
  const opsNarr = polishProse(ops.notes);
  if (opsNarr) push(`Narrative: ${opsNarr}`);
  const accomp = polishProse(ops.accomplishments);
  if (accomp) push(`Main Accomplishments: ${accomp}`);
  const probs = polishProse(ops.problems);
  if (probs) push(`Problems Encountered: ${probs}`);
  const corr = polishProse(ops.correctiveActions);
  if (corr) push(`Corrective Actions: ${corr}`);
  push(`Current Status: ${orNull(ops.currentStatus) ? polishProse(ops.currentStatus) : MISSING}`);
  push(`Plan for Next Tour / Next 24 Hours: ${orNull(ops.nextPlan) ? polishProse(ops.nextPlan) : MISSING}`);

  // ----- 24-Hour Activity Breakdown -----
  pushSection("24-Hour Activity Breakdown");
  const acts = (state.activities || []).filter((a) => a.start || a.end || a.activityType || a.notes);
  if (acts.length === 0) {
    push(MISSING);
  } else {
    acts.forEach((a, i) => {
      const dur = a.duration ? `${a.duration} h` : "(duration —)";
      push(`${String(i + 1).padStart(2, "0")}. ${orMissing(a.start)} – ${orMissing(a.end)}  ${dur}  ·  ${orMissing(a.activityType)}${a.npt ? "  [NPT]" : ""}`);
      if (a.depthFrom || a.depthTo) push(`    Depth: ${orMissing(a.depthFrom)} ${depthU} → ${orMissing(a.depthTo)} ${depthU}`);
      const narr = polishProse(a.notes);
      if (narr) push(`    Notes: ${narr}`);
      if (a.responsible) push(`    Responsible: ${a.responsible}`);
      const cmts = polishProse(a.comments);
      if (cmts) push(`    Comments: ${cmts}`);
    });
    const sums = sumActivities(acts);
    push(`\nTotal Reported Hours: ${sums.total} h`);
    push(`Total NPT Hours: ${sums.npt} h`);
    push(`Total Standby Hours: ${sums.standby} h`);
    if (Math.abs(sums.total - 24) > 0.05) {
      push(`Note: Total reported hours (${sums.total}) does not equal 24.00 — review activity entries.`);
    }
  }

  // ----- Drilling Parameters -----
  pushSection("Drilling Parameters");
  const p = state.params || {};
  const paramRow = (label, key, impU, metU) => {
    const u = unitFor(sys, impU, metU);
    push(`${label}: ${orMissing(p[key])} ${p[key] ? u : ""}`);
  };
  paramRow("Weight on Bit", "wob", "klb", "kN");
  paramRow("Rotary RPM", "rotaryRpm", "RPM", "RPM");
  paramRow("Top Drive RPM", "tdRpm", "RPM", "RPM");
  paramRow("Torque", "torque", "kft·lb", "kN·m");
  paramRow("Pump Pressure", "pumpPressure", "psi", "bar");
  paramRow("Flow Rate", "flowRate", "gpm", "L/min");
  paramRow("SPM", "spm", "spm", "spm");
  paramRow("Pump Output", "pumpOutput", "bbl/stk", "m³/stk");
  paramRow("Hookload", "hookload", "klb", "kN");
  paramRow("ROP Average", "ropAvg", "ft/hr", "m/hr");
  paramRow("ROP Instantaneous", "ropInst", "ft/hr", "m/hr");
  paramRow("Bit Depth", "bitDepth", "ft", "m");
  paramRow("Hole Depth", "holeDepth", "ft", "m");
  paramRow("Standpipe Pressure", "spp", "psi", "bar");
  paramRow("ECD", "ecd", "ppg", "kg/m³");
  paramRow("Annular Velocity", "annVel", "ft/min", "m/min");

  // ----- Mud / Drilling Fluid -----
  pushSection("Mud / Drilling Fluid Summary");
  const m = state.mud || {};
  push(`Mud Type: ${orMissing(m.mudType)}`);
  push(`Mud Weight: ${orMissing(m.mudWeight)} ${m.mudWeight ? unitFor(sys, "ppg", "kg/m³") : ""}`);
  push(`Funnel Viscosity: ${orMissing(m.funnelVisc)} s/qt`);
  push(`PV: ${orMissing(m.pv)} cP  ·  YP: ${orMissing(m.yp)} lb/100ft²`);
  push(`Gel Strengths (10s/10min/30min): ${orMissing(m.gels)}`);
  push(`pH: ${orMissing(m.ph)}  ·  Chlorides: ${orMissing(m.chlorides)} mg/L`);
  push(`Sand: ${orMissing(m.sand)} %  ·  Solids: ${orMissing(m.solids)} %`);
  if (m.owr) push(`Oil / Water Ratio (OBM): ${m.owr}`);
  push(`Fluid Loss: ${orMissing(m.fluidLoss)} mL/30min`);
  if (m.mbt) push(`MBT: ${m.mbt}`);
  push(`Mud Temperature: ${orMissing(m.mudTemp)} ${m.mudTemp ? unitFor(sys, "°F", "°C") : ""}`);
  push(`Active Pit Volume: ${orMissing(m.activePit)}  ·  Reserve Pit Volume: ${orMissing(m.reservePit)}`);
  push(`Gains / Losses: ${orMissing(m.gainsLosses)}`);
  const treat = polishProse(m.treatments);
  if (treat) push(`Treatments Added: ${treat}`);
  const mudEngNotes = polishProse(m.notes);
  if (mudEngNotes) push(`Mud Engineer Notes: ${mudEngNotes}`);

  // ----- Bit / BHA -----
  pushSection("Bit / BHA Summary");
  const b = state.bha || {};
  push(`Bit Number: ${orMissing(b.bitNumber)}  ·  Size: ${orMissing(b.bitSize)}  ·  Type: ${orMissing(b.bitType)}`);
  push(`Manufacturer: ${orMissing(b.manufacturer)}  ·  IADC Code: ${orMissing(b.iadcCode)}`);
  push(`Jets / Nozzles: ${orMissing(b.jets)}  ·  TFA: ${orMissing(b.tfa)}`);
  const bhaDesc = polishProse(b.bhaDescription);
  if (bhaDesc) push(`BHA Description: ${bhaDesc}`);
  if (b.motorRss) push(`Motor / RSS: ${b.motorRss}`);
  if (b.stabilizers) push(`Stabilizers: ${b.stabilizers}`);
  if (b.mwdLwd) push(`MWD / LWD Tools: ${b.mwdLwd}`);
  push(`Bit Hours: ${orMissing(b.bitHours)}  ·  Drilled Interval: ${orMissing(b.drilledInterval)}  ·  Avg ROP: ${orMissing(b.avgRop)}`);
  if (b.dullGrade) push(`Dull Grade (if pulled): ${b.dullGrade}`);
  const bhaNotes = polishProse(b.notes);
  if (bhaNotes) push(`BHA Notes: ${bhaNotes}`);

  // ----- Pumps and Surface Equipment -----
  pushSection("Equipment Status");
  const e = state.equipment || {};
  push(`Pump 1: ${orMissing(e.pump1)}  ·  Pump 2: ${orMissing(e.pump2)}  ·  Pump 3: ${orMissing(e.pump3)}`);
  push(`Liner Size: ${orMissing(e.linerSize)}  ·  Stroke Length: ${orMissing(e.strokeLength)}  ·  SPM: ${orMissing(e.spm)}  ·  Pump Output: ${orMissing(e.pumpOutput)}`);
  const surfIssues = polishProse(e.surfaceIssues);
  if (surfIssues) push(`Surface Equipment Issues: ${surfIssues}`);
  const repairs = polishProse(e.repairs);
  if (repairs) push(`Repairs Performed: ${repairs}`);
  const eqNotes = polishProse(e.notes);
  if (eqNotes) push(`Equipment Notes: ${eqNotes}`);

  // ----- BOP / Well Control -----
  pushSection("Well Control / BOP Status");
  const bop = state.bop || {};
  push("Recorded status only. Final verification must be performed under approved procedures by competent personnel.");
  push(`BOP Status: ${orMissing(bop.bopStatus)}`);
  push(`BOP Test Performed: ${_yn(bop.bopTest)}  ·  BOP Drill Performed: ${_yn(bop.bopDrill)}`);
  push(`Choke Manifold: ${orMissing(bop.chokeManifold)}  ·  Accumulator: ${orMissing(bop.accumulator)}  ·  Trip Tank: ${orMissing(bop.tripTank)}`);
  push(`Flow Check Performed: ${_yn(bop.flowCheck)}  ·  Well Shut In: ${_yn(bop.shutIn)}  ·  Kick / Influx Observed: ${_yn(bop.kickObserved, "Yes — escalate")}`);
  const wcNotes = polishProse(bop.notes);
  if (wcNotes) push(`Well Control Notes: ${wcNotes}`);
  const safetyEq = polishProse(bop.safetyEqIssues);
  if (safetyEq) push(`Safety Critical Equipment Issues: ${safetyEq}`);
  if (bop.kickObserved || bop.shutIn) {
    push("Notify company well-control representative. Follow approved well-control procedures and regulatory notification requirements.");
  }

  // ----- HSE -----
  pushSection("HSE Summary");
  const hse = state.hse || {};
  const incidents = polishProse(hse.incidents);
  const nearMisses = polishProse(hse.nearMisses);
  const noIncidents = !incidents && !nearMisses && !hse.spill;
  if (noIncidents) push("No HSE incidents or near misses reported during this report period.");
  if (incidents) push(`Incidents: ${incidents}`);
  if (nearMisses) push(`Near Misses: ${nearMisses}`);
  if (hse.stopCards) push(`STOP Cards / Observations: ${hse.stopCards}`);
  if (hse.toolboxTalks) push(`Toolbox Talks Completed: ${hse.toolboxTalks}`);
  if (hse.safetyMeetings) push(`Safety Meetings: ${hse.safetyMeetings}`);
  const ptw = polishProse(hse.ptw);
  if (ptw) push(`Permit-to-Work Concerns: ${ptw}`);
  const simops = polishProse(hse.simops);
  if (simops) push(`SIMOPS Concerns: ${simops}`);
  const wx = polishProse(hse.weatherConcerns);
  if (wx) push(`Weather Concerns: ${wx}`);
  if (hse.spill) push(`Environmental Spills / Releases: ${hse.spill}`);
  if (hse.dropped) push(`Dropped-Object Concerns: ${hse.dropped}`);
  if (hse.ppe) push(`PPE Concerns: ${hse.ppe}`);
  const hseNotes = polishProse(hse.notes);
  if (hseNotes) push(`HSE Notes: ${hseNotes}`);
  if (incidents || hse.spill) {
    push("Follow company incident reporting and regulatory notification requirements.");
  }

  // ----- POB / Personnel -----
  pushSection("Personnel / POB");
  const pob = state.pob || {};
  push(`Operator Personnel: ${orMissing(pob.operator)}`);
  push(`Contractor Personnel: ${orMissing(pob.contractor)}`);
  push(`Service Company Personnel: ${orMissing(pob.service)}`);
  push(`Catering / Marine / Logistics: ${orMissing(pob.support)}`);
  push(`Visitors: ${orMissing(pob.visitors)}`);
  push(`Total POB: ${orMissing(pob.totalPob)}`);
  const crew = polishProse(pob.crewChanges);
  if (crew) push(`Crew Changes: ${crew}`);
  const pobNotes = polishProse(pob.notes);
  if (pobNotes) push(`Personnel Notes: ${pobNotes}`);

  // ----- Logistics -----
  pushSection("Logistics / Materials");
  const lg = state.logistics || {};
  push(`Fuel Received: ${orMissing(lg.fuelReceived)}  ·  Fuel Consumed: ${orMissing(lg.fuelConsumed)}`);
  push(`Bulk Received: ${orMissing(lg.bulkReceived)}  ·  Cement: ${orMissing(lg.cement)}  ·  Barite: ${orMissing(lg.barite)}  ·  Bentonite: ${orMissing(lg.bentonite)}`);
  push(`Chemicals: ${orMissing(lg.chemicals)}  ·  Water: ${orMissing(lg.water)}`);
  push(`Casing / Tubulars Received: ${orMissing(lg.tubularsReceived)}`);
  if (lg.boatsHelis) push(`Boats / Helicopters: ${lg.boatsHelis}`);
  const lgNotes = polishProse(lg.notes);
  if (lgNotes) push(`Logistics Notes: ${lgNotes}`);

  // ----- Weather -----
  pushSection("Weather / Marine");
  const wx2 = state.weather || {};
  push(`Weather: ${orMissing(wx2.weather)}`);
  push(`Wind: ${orMissing(wx2.wind)}  ·  Sea State: ${orMissing(wx2.seaState)}  ·  Swell: ${orMissing(wx2.swell)}  ·  Current: ${orMissing(wx2.current)}`);
  push(`Visibility: ${orMissing(wx2.visibility)}  ·  Temperature: ${orMissing(wx2.temp)}`);
  push(`Waiting on Weather: ${_yn(wx2.wow)}`);
  const wxNotes = polishProse(wx2.notes);
  if (wxNotes) push(`Marine Notes: ${wxNotes}`);

  // ----- NPT / Downtime -----
  pushSection("NPT / Downtime");
  const npt = state.npt || {};
  push(`NPT Occurred: ${_yn(npt.occurred)}`);
  if (npt.occurred) {
    push(`Start: ${orMissing(npt.start)}  ·  End: ${orMissing(npt.end)}  ·  Duration: ${orMissing(npt.duration)} h`);
    push(`Category: ${orMissing(npt.category)}  ·  Equipment/System: ${orMissing(npt.equipment)}`);
    const desc = polishProse(npt.description);
    if (desc) push(`Description: ${desc}`);
    push(`Cause: ${orNull(npt.cause) ? polishProse(npt.cause) : "Cause under review."}`);
    const action = polishProse(npt.action);
    if (action) push(`Action Taken: ${action}`);
    if (npt.responsible) push(`Responsible Department: ${npt.responsible}`);
    const prev = polishProse(npt.preventive);
    if (prev) push(`Preventive Action: ${prev}`);
    push(`Follow-up Required: ${_yn(npt.followUp)}`);
  }

  // ----- Plan for next 24 hours -----
  pushSection("Plan for Next 24 Hours");
  push(orNull(ops.nextPlan) ? polishProse(ops.nextPlan) : MISSING);

  // ----- Footer placeholders -----
  pushSection("Prepared By / Reviewed By");
  push(`Prepared By: ${orMissing(h.preparedBy)} (${orMissing(h.position)})`);
  push("Reviewed By: ____________________________");
  push("Approved By: ____________________________");

  return lines.join("\n");
}

// ----- 4. Missing-data review checklist --------------------------------------
export function reviewChecklist(state) {
  const out = [];
  const h = state.header || {};
  const w = state.well || {};
  const ops = state.ops || {};
  const hse = state.hse || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.preparedBy) out.push("Prepared-By name missing");
  if (!w.currentMd) out.push("Current depth (MD) missing");
  if (!ops.nextPlan) out.push("Next 24-hour plan missing");
  const haveAnyHse =
    hse.incidents || hse.nearMisses || hse.stopCards || hse.toolboxTalks || hse.safetyMeetings || hse.notes;
  if (!haveAnyHse) out.push("HSE section not completed");
  const sums = sumActivities(state.activities || []);
  if (sums.total > 0 && Math.abs(sums.total - 24) > 0.05) {
    out.push(`Activity total does not equal 24 h (currently ${sums.total} h)`);
  }
  return out;
}

// ----- 5. Quick phrase buttons -----------------------------------------------
export const QUICK_PHRASES = [
  "No HSE incidents reported.",
  "Operations continued as planned.",
  "No NPT recorded.",
  "Equipment returned to service.",
  "Awaiting spare parts.",
  "Continued drilling ahead.",
  "Circulated bottoms up.",
  "Flow check performed, well static.",
  "BOP drill completed.",
  "Weather standby.",
  "Repairs completed.",
  "Plan: continue drilling next tour.",
];

// ----- 6. Future-LLM polish hook ---------------------------------------------
// When you're ready to wire Emergent LLM key (Claude / Gemini) for live polish,
// implement an async `aiPolish(stateOrText)` and swap in callers. This hook
// keeps the surface stable. NOTE: do not invent facts the user didn't enter.
//
// export async function aiPolish(text) {
//   // 1. call Emergent LLM key with a strict "polish only, do not invent" prompt
//   // 2. return the polished string
// }
